import type { AppraisalAnnexureValues, EmployeeAppraisalValues } from "./types.ts";

export function amount(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0;
}

function roundMoney(value: number): number {
  return Math.round(value);
}

export const ESIC_GROSS_LIMIT = 42_000;

export function isEsicEligible(monthlyGross: number): boolean {
  return monthlyGross > 0 && monthlyGross <= ESIC_GROSS_LIMIT;
}

export function esicEmployeeFromBasic(basic: number): number {
  return roundMoney(Math.max(0, basic) * 0.0075);
}

export function esicEmployerFromBasic(basic: number): number {
  return roundMoney(Math.max(0, basic) * 0.0325);
}

export function basicFromMonthlyGross(monthlyGross: number, internetReimbursement: number): number {
  return roundMoney(Math.max(0, monthlyGross - internetReimbursement) * 0.5);
}

export function providentFundFromBasic(basic: number): number {
  const normalizedBasic = Math.max(0, basic);
  return normalizedBasic <= 25_000
    ? roundMoney(normalizedBasic * 0.12)
    : 3_000;
}

export function annualBenefitsFromBasic(basic: number, pfEmployerPart: number, esicEmployerPart = 0): number {
  return roundMoney(Math.max(0, basic) / 2) + (roundMoney(Math.max(0, pfEmployerPart)) + roundMoney(Math.max(0, esicEmployerPart))) * 12;
}

export const APPRAISAL_FIXED_AMOUNTS = {
  pfEmployeePart: 1800,
  internetReimbursement: 1500,
  pfEmployerPart: 1800,
} as const;

export function roundAmountInput(value: string): string {
  return value.trim() === "" ? "" : String(amount(value));
}

export function annualAmountToRoundedMonthly(annualAmount: number): number {
  return Math.round(Math.max(0, annualAmount) / 12);
}

function annualComponentRemarks(value: string): string | null {
  const trimmed = value.trim();
  const normalized = trimmed.toLowerCase().replace(/[.*]+$/, "");
  if (
    !trimmed ||
    normalized === "annual fixed component shall be paid at the completion of 1 year" ||
    normalized === "annual fixed component shall be paid on the completion of one year."
  ) return null;
  return trimmed;
}

export function appraisalCompensationSummary(values: AppraisalAnnexureValues) {
  const monthlyGross = amount(values.monthlyGross);
  const pfEmployeePart = amount(values.pfEmployeePart);
  const internetReimbursement = amount(values.internetReimbursement);
  const pfEmployerPart = amount(values.pfEmployerPart);
  const annualFixedComponent = values.annualComponentEnabled
    ? amount(values.annualComponentAmount)
    : 0;
  // A+B+C is entered monthly. Remaining components and Annual CTC are derived.
  const basic = basicFromMonthlyGross(monthlyGross, internetReimbursement);
  const houseRentAllowance = roundMoney(basic / 2);
  const esicEligible = isEsicEligible(monthlyGross);
  const esicEmployeePart = esicEligible ? esicEmployeeFromBasic(basic) : 0;
  const esicEmployerPart = esicEligible ? esicEmployerFromBasic(basic) : 0;
  const specialAllowance = roundMoney(Math.max(
    0,
    monthlyGross - basic - pfEmployeePart - esicEmployeePart - houseRentAllowance - internetReimbursement,
  ));
  const gratuity = roundMoney((basic / 2) / 12);
  const monthlyBenefits = roundMoney(gratuity + pfEmployerPart + esicEmployerPart);
  const monthlyAnnualFixedComponent = annualAmountToRoundedMonthly(annualFixedComponent);
  const monthlyCtc = roundMoney(monthlyGross + monthlyBenefits + monthlyAnnualFixedComponent);
  const annualGross = roundMoney(monthlyGross * 12);
  const annualBenefits = annualBenefitsFromBasic(basic, pfEmployerPart, esicEmployerPart);
  return {
    monthlyGross, monthlyBenefits, monthlyCtc,
    annualGross, annualBenefits,
    annualCtc: roundMoney(annualGross + annualBenefits + annualFixedComponent),
    takeHome: roundMoney(Math.max(0, monthlyGross - pfEmployeePart - esicEmployeePart)),
    esicEligible,
    components: {
      basic,
      pfEmployeePart,
      esicEmployeePart,
      specialAllowance,
      houseRentAllowance,
      internetReimbursement,
      gratuity,
      pfEmployerPart,
      esicEmployerPart,
      annualFixedComponent,
    },
  };
}

function localIso(date: Date | null): string {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function createAppraisalDraftPayload(
  basic: EmployeeAppraisalValues,
  annexure: AppraisalAnnexureValues,
  salutation: string,
): Record<string, unknown> {
  const calculated = appraisalCompensationSummary(annexure);
  const components = calculated.components;
  return {
    employeeId: Number(basic.employeeId), letterType: basic.letterType,
    baseValue: calculated.monthlyGross,
    letterIssueDate: localIso(basic.letterIssueDate), effectiveDate: localIso(basic.effectiveDate), salutation,
    compensation: { basic: components.basic, pfEmployeePart: components.pfEmployeePart, esic: components.esicEmployeePart },
    allowances: {
      specialAllowance: components.specialAllowance, houseRentAllowance: components.houseRentAllowance,
      conveyanceAllowance: amount(annexure.conveyanceAllowance), helperAndDriver: amount(annexure.helperAndDriver),
      lta: amount(annexure.lta), mealReimbursement: amount(annexure.mealReimbursement),
      internetReimbursement: components.internetReimbursement, gymHealthClubReimbursement: amount(annexure.gymHealthClubReimbursement),
    },
    statutory: { gratuity: components.gratuity, pfEmployerPart: components.pfEmployerPart, esic: components.esicEmployerPart },
    annualComponent: { enabled: annexure.annualComponentEnabled, amount: components.annualFixedComponent, remarks: annualComponentRemarks(annexure.annualComponentRemarks) },
    noticePeriod: basic.noticePeriod || null,
    designationChange: basic.letterType !== "APPRAISAL",
    designationId: basic.designationId ? Number(basic.designationId) : null,
    designationChangeText: basic.letterType !== "APPRAISAL" ? basic.designation.trim() || null : null,
  };
}
